"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getCookie, setCookie } from "@/lib/utils/cookies";

// v2: the popup now offers the sourcing guide, so anyone who dismissed or
// clicked the old price-guide popup should see this one once.
const DISMISSED_COOKIE = "padi_sourcing_popup_dismissed";
const CONVERTED_KEY = "padi_sourcing_popup_converted";
const SHOW_DELAY_MS = 3000;
const DISMISS_DAYS = 14;

// The sourcing quote form now doubles as the guide sign-up.
const SOURCING_PATH = "/sourcing";

export function LeadCapturePopup() {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Never on the sourcing page itself: the visitor is already on the form.
  const onSourcing = usePathname()?.startsWith(SOURCING_PATH) ?? false;

  useEffect(() => {
    if (onSourcing) {
      setOpen(false);
      return;
    }
    const alreadyDismissed = getCookie(DISMISSED_COOKIE) !== null;
    const alreadyConverted =
      typeof window !== "undefined" &&
      window.localStorage.getItem(CONVERTED_KEY) === "1";
    if (alreadyDismissed || alreadyConverted) return;

    timerRef.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [onSourcing]);

  const dismiss = () => {
    setCookie(DISMISSED_COOKIE, "1", DISMISS_DAYS);
    setOpen(false);
  };

  const clickThrough = () => {
    window.localStorage.setItem(CONVERTED_KEY, "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="p-0 gap-0 overflow-hidden sm:max-w-3xl grid-cols-1 sm:grid-cols-[42%_1fr]">
        <div className="relative h-40 sm:h-full">
          <Image
            src="/images/popup/soybeans.jpg"
            alt="Soybeans"
            fill
            className="object-cover"
            sizes="(min-width: 640px) 42vw, 100vw"
          />
        </div>

        <div className="relative flex flex-col justify-center overflow-hidden p-8 sm:p-10">
          <span
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-montserrat text-6xl sm:text-8xl font-extrabold text-primary/[0.06] select-none"
          >
            GUIDE
          </span>

          <h2 className="relative max-w-[30ch] text-2xl sm:text-3xl font-extrabold font-montserrat leading-tight text-balance">
            Looking for Reliable Agro Commodity Suppliers in Africa?
          </h2>
          <p className="relative mt-3 max-w-[34ch] text-muted-foreground">
            Request a sourcing quote and get our free Risk-Free Guide to
            Sourcing African Commodities.
          </p>

          <Link
            href={`${SOURCING_PATH}#request-quote`}
            onClick={clickThrough}
            className="relative mt-6 flex w-fit max-w-full items-center gap-4 rounded-lg bg-primary py-3.5 pl-5 pr-3.5 font-bold text-primary-foreground transition-colors hover:bg-primary-light"
          >
            Get my FREE sourcing guide
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20">
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
